import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import { formatDate } from '../../../../utils/collateralFormatters';
import useLegalCaseCourtDecisions, { type AddCourtDecisionValue } from '../../../../hooks/useLegalCaseCourtDecisions';
import type { CourtDecision } from '../../../../types/LegalCase';
import LegalCaseCourtDecisionForm from './LegalCaseCourtDecisionForm';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseCourtDecisionsProps {
    caseId: string;
    initialDecisions?: CourtDecision[];
}

// Uma linha de decisão. Extraída para fora do componente (diretriz: sem função dentro de
// função). A decisão é fato congelado — mostra tipo, data e teor; sem dropdown de status.
const CourtDecisionItem = ({
    decision,
    onRemove,
    disabled,
}: {
    decision: CourtDecision;
    onRemove: () => void;
    disabled: boolean;
}) => (
    <li className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
        <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{decision.decisionTypeFormatted}</span>
                {decision.decisionDate && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(decision.decisionDate)}</span>
                )}
            </div>
            {decision.summary && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">{decision.summary}</p>
            )}
        </div>
        <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            title="Remover decisão"
            className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
        >
            <FaTrash size={11} />
        </button>
    </li>
);

// Seção "Decisões" do detalhe do caso. Lista as decisões judiciais (sentença, acórdão,
// liminar, homologação, despacho), da mais recente para a mais antiga. É entidade
// independente da audiência (uma decisão pode nascer fora de audiência). A remoção passa por
// ConfirmDialog (padrão do módulo).
const LegalCaseCourtDecisions = ({ caseId, initialDecisions }: LegalCaseCourtDecisionsProps) => {
    const decisions = useLegalCaseCourtDecisions(caseId, initialDecisions);
    const [showForm, setShowForm] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const openForm = () => {
        decisions.clearErrors();
        setShowForm(true);
    };

    const closeForm = () => {
        decisions.clearErrors();
        setShowForm(false);
    };

    const submitDecision = async (value: AddCourtDecisionValue): Promise<boolean> => {
        const ok = await decisions.addDecision(value);
        if (ok) closeForm();
        return ok;
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await decisions.removeDecision(pendingRemoval);
        setPendingRemoval(null);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Decisões judiciais
                </h4>
                {!showForm && (
                    <button
                        type="button"
                        onClick={openForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Decisão
                    </button>
                )}
            </div>

            {decisions.decisions.length === 0 && !showForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhuma decisão registrada</p>
            )}

            {decisions.decisions.length > 0 && (
                <ul>
                    {decisions.decisions.map((decision) => (
                        <CourtDecisionItem
                            key={decision.id}
                            decision={decision}
                            onRemove={() => setPendingRemoval(decision.id)}
                            disabled={decisions.isSaving}
                        />
                    ))}
                </ul>
            )}

            {showForm && (
                <LegalCaseCourtDecisionForm
                    typeOptions={decisions.typeOptions}
                    errorFields={decisions.errorFields}
                    isSaving={decisions.isSaving}
                    onSubmit={submitDecision}
                    onCancel={closeForm}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover decisão"
                message="Remover esta decisão do caso? Esta ação não pode ser desfeita."
                isBusy={decisions.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseCourtDecisions;
